<script setup lang="ts">
import SecondaryDrawerHeader from "@/modules/explorer/components/SecondaryDrawer/SecondaryDrawerHeader.vue";
import { ref, computed, watch } from "vue";
import { useExplorerStore } from "@/modules/explorer/store";
import { LEGEND_POSITIONS, LABEL_VALUES } from "chartjs-graph";
import { required, numericAndFloat } from "@/ui/validation/validationRules";
import { useI18n } from "vue-i18n";
import Switch from "@/ui/components/switch/switch.vue";
import SettingsRow from "@/modules/explorer/components/SecondaryDrawer/SettingsRow.vue";
import SettingsRowInput from "@/modules/explorer/components/SecondaryDrawer/SettingsRowInput.vue";
import { effectiveState } from "@/Utils/proxy/DerivedModelProxyHandler";
import { useBaseStore } from "@/stores/base";
import LabelScaleFormat = App.Enums.LabelScaleFormat;
import { debounce } from "vue-debounce";
import LocaleNumberInput from "@/ui/components/LocaleNumberInput.vue";
import { hierarchyNamingStyleOptions } from "@/modules/explorer/components/SecondaryDrawer/displaySettingsOptions";

const { t } = useI18n();
const store = useExplorerStore();
const openExpansionPanels = ref([0, 1, 2, 3, 4, 5]);

const docShowDelta = effectiveState(
	() => store.document.date_comparison,
	() => store.derivedDocument.date_comparison,
	"showDeltaAs"
);

const showDelta = computed({
	get() {
		return docShowDelta.value !== "disabled";
	},
	set(v) {
		if (v) {
			return (docShowDelta.value = "primary");
		}

		docShowDelta.value = "disabled";
	},
});

const showDeltaAsSecondary = computed({
	get() {
		return docShowDelta.value === "secondary";
	},
	set(v) {
		if (v) {
			return (docShowDelta.value = "secondary");
		}

		docShowDelta.value = "primary";
	},
});

const legendOptions = ref([
	{ id: LEGEND_POSITIONS.TOP, name: "Top" },
	{ id: LEGEND_POSITIONS.RIGHT, name: "Right" },
	{ id: LEGEND_POSITIONS.BOTTOM, name: "Bottom" },
	{ id: LEGEND_POSITIONS.LEFT, name: "Left" },
]);

const labelValues = ref([
	{ id: LABEL_VALUES.AMOUNT, name: "Amount" },
	{ id: LABEL_VALUES.PERCENTAGE, name: "Percentage of Stack" },
	{ id: LABEL_VALUES.PERCENTAGE_OF_TOTAL, name: "Percentage of Total" },
	{ id: LABEL_VALUES.CATEGORY, name: "Category" },
]);

const totalLabelValues = ref([
	{ id: LABEL_VALUES.AMOUNT, name: "Amount" },
	{ id: LABEL_VALUES.PERCENTAGE, name: "Percentage" },
	{ id: LABEL_VALUES.PERCENTAGE_OF_TOTAL, name: "Percentage of Total" },
]);

const labelScaleFormats: { id: LabelScaleFormat; name: string }[] = [
	{ id: "auto", name: "Auto" },
	{ id: "none", name: "None" },
	{ id: "thousands", name: "Thousands" },
	{ id: "millions", name: "Millions" },
];

const deltaOptions = [
	{ id: true, name: "Absolute" },
	{ id: false, name: "Relative" },
];

const isAggregateHorizontalRemainingCategoriesEnabledVisible = effectiveState(
	() => store.document,
	() => store.derivedDocument,
	"is_aggregate_horizontal_remaining_categories_enabled"
);
const isHorizontalAggregationAvailable = computed(() => {
	return store.document.vertical.is_summarizable;
});

const horizontal_top_x = computed({
	get() {
		return store.document.horizontal_top_x;
	},
	set(user_input: string) {
		store.document.horizontal_top_x = cleanUserInput(user_input);
	},
});

const period_build_up_window = computed({
	get() {
		return store.document.period_build_up_window;
	},
	set(user_input: string) {
		store.document.period_build_up_window = cleanUserInput(user_input);
	},
});

/*
 * The transaction snapshot axis walks recording time and hangs off the transaction snapshot instead
 * of the subject period, so it has its own range: how far back from that snapshot the axis reaches.
 */
const showTransactionBuildUpRange = computed(
	() =>
		store.derivedDocument.horizontal_dimension?.snapshot_axis === "transaction"
);

const transaction_build_up_days_before = computed({
	get() {
		return store.document.transaction_build_up_days_before;
	},
	set(user_input: string) {
		store.document.transaction_build_up_days_before = cleanUserInput(
			user_input,
			0
		);
	},
});

const transaction_build_up_days_after = computed({
	get() {
		return store.document.transaction_build_up_days_after;
	},
	set(user_input: string) {
		store.document.transaction_build_up_days_after = cleanUserInput(
			user_input,
			0
		);
	},
});

const aggregate_legend_after = computed({
	get() {
		return store.document.aggregate_legend_after;
	},
	set(user_input: string) {
		store.document.aggregate_legend_after = cleanUserInput(user_input);
	},
});

const cleanUserInput = (input: string, defaultValue: number = 5) => {
	const parsed = parseInt(input);
	return isNaN(parsed) ? defaultValue : parsed;
};

watch(
	() => store.document.is_custom_boundaries_enabled,
	(is_custom_boundaries_enabled) => {
		if (is_custom_boundaries_enabled) {
			store.document.custom_boundary_max =
				store.explorerState.highestMetricValue;
			store.document.custom_boundary_min =
				store.explorerState.lowestMetricValue;
		}
	}
);

const periodItems = computed(() => {
	return useBaseStore().currentEnvironment.product_plan.name_clean === "parking"
		? [180, 120, 90, 60, 30]
		: [360, 270, 180, 120, 90, 60, 30];
});

watch(
	() => store.document.is_horizontal_sorting_enabled,
	(is_horizontal_sorting_enabled) => {
		if (!is_horizontal_sorting_enabled) {
			store.document.is_horizontal_top_x_enabled = false;
		}
	}
);

/*
 * "Visualised as percentage share" is the only percentage that doesn't need to be mulitplied by 100
 * This prevents the custom boundaries from becoming 1% or 10.000% when swapping between that and other percentages
 */
watch(
	() => store.derivedDocument.is_visualised_as_percentage_share,
	(nv, ov) => {
		if (nv === ov) return;

		const multiplier = ov ? 0.01 : 100;

		store.document.custom_boundary_min *= multiplier;
		store.document.custom_boundary_max *= multiplier;
	}
);

const percentageMultiplier = computed(() =>
	store.derivedDocument.is_visualised_as_percentage_share ? 0.01 : 1
);

const debouncedMax = debounce((val: number) => {
	store.document.custom_boundary_max = val / percentageMultiplier.value;
}, 100);
const customBoundaryMax = computed({
	get: () => store.document.custom_boundary_max * percentageMultiplier.value,
	set: debouncedMax,
});

const debouncedMin = debounce((val: number) => {
	store.document.custom_boundary_min = val / percentageMultiplier.value;
}, 100);
const customBoundaryMin = computed({
	get: () => store.document.custom_boundary_min * percentageMultiplier.value,
	set: debouncedMin,
});

const docFormatForGraph = effectiveState(
	() => store.document,
	() => store.derivedDocument,
	"formatForGraph"
);

const docSecondaryVertical = effectiveState(
	() => store.document,
	() => store.derivedDocument,
	"secondary_vertical"
);

const synchronisedYAxis = effectiveState(
	() => store.document,
	() => store.derivedDocument,
	"is_y_axis_synchronised"
);

const aggregateLegend = effectiveState(
	() => store.document,
	() => store.derivedDocument,
	"is_aggregate_legend_enabled"
);

const isAggregateLegendAvailable = computed(() => {
	return (
		useExplorerStore().document?.legend_dimension &&
		useExplorerStore().document.legend_dimension.name_clean !==
			useExplorerStore().document.horizontal_dimension.name_clean
	);
});

const disableSyncYAxis = computed(() => {
	return (
		!docSecondaryVertical.value ||
		docFormatForGraph.value !== docSecondaryVertical.value.format
	);
});

const disableShowDeltaAsSecondaryVertical = computed(() => {
	return docSecondaryVertical.value && !showDeltaAsSecondary.value;
});
</script>

<template>
	<SecondaryDrawerHeader icon="mdi-tune" title="Display Settings" />
	<perfect-scrollbar :options="{ suppressScrollX: true }">
		<v-expansion-panels v-model="openExpansionPanels" multiple>
			<!-- Graph Visualisation Panel -->
			<v-expansion-panel
				class="mt-0 expansion-panel-border"
				elevation="0"
				:key="'graph'"
			>
				<v-expansion-panel-title class="setting-header-format">
					<span>Graph Visualisation</span>
					<v-spacer />
				</v-expansion-panel-title>
				<v-expansion-panel-text>
					<!-- Show Value Labels -->
					<SettingsRow title="Show value labels">
						<Switch v-model="store.document.is_label_visible" hide-details />
					</SettingsRow>

					<v-expand-transition>
						<settings-row-input
							v-if="store.document.is_label_visible"
							class="setting-details-format"
						>
							<v-select
								v-if="store.document.is_label_visible"
								v-model="store.document.label_value"
								:items="labelValues"
								item-title="name"
								item-value="id"
								variant="filled"
								hide-details="auto"
								density="compact"
							/>
						</settings-row-input>
					</v-expand-transition>

					<!-- Show Total Value Labels -->
					<SettingsRow title="Show total value labels">
						<Switch
							v-model="store.document.is_total_label_visible"
							hide-details
						/>
					</SettingsRow>

					<v-expand-transition>
						<settings-row-input
							v-if="store.document.is_total_label_visible"
							class="setting-details-format"
						>
							<v-select
								v-model="store.document.total_label_value"
								:items="totalLabelValues"
								item-title="name"
								item-value="id"
								variant="filled"
								hide-details="auto"
								density="compact"
							/>
						</settings-row-input>
					</v-expand-transition>

					<!-- Label Scale Formatting -->
					<SettingsRow
						v-if="
							store.document.is_label_visible ||
							store.document.is_total_label_visible
						"
						title="Value resolution"
					/>
					<settings-row-input
						v-if="
							store.document.is_label_visible ||
							store.document.is_total_label_visible
						"
					>
						<v-expand-transition>
							<v-select
								title="Label Scale Format"
								v-model="store.document.label_scale_format"
								:items="labelScaleFormats"
								item-title="name"
								item-value="id"
								variant="filled"
								hide-details="auto"
								density="compact"
							/>
						</v-expand-transition>
					</settings-row-input>

					<!-- Show Label in Box -->
					<SettingsRow title="Show label in box">
						<Switch
							v-model="store.document.is_label_box_visible"
							hide-details
						/>
					</SettingsRow>

					<SettingsRow title="Rotate category labels">
						<Switch
							v-model="store.document.is_horizontal_label_rotation_enabled"
							hide-details
						/>
					</SettingsRow>

					<v-expand-transition>
						<settings-row-input
							v-if="store.document.is_horizontal_label_rotation_enabled"
							class="setting-details-format"
						>
							<v-select
								density="compact"
								v-model="store.document.horizontal_label_rotation_degrees"
								:items="[
									{ name: 'Horizontal', value: 0 },
									{ name: 'Tilted', value: -45 },
									{ name: 'Vertical', value: -90 },
								]"
								item-title="name"
								item-value="value"
								variant="filled"
								hide-details
							/>
						</settings-row-input>
					</v-expand-transition>
				</v-expansion-panel-text>
			</v-expansion-panel>

			<!-- Vertical Axis Panel -->
			<v-expansion-panel
				class="mt-0 expansion-panel-border"
				elevation="0"
				:key="'vertical'"
			>
				<v-expansion-panel-title class="setting-header-format">
					<span>Vertical Axis</span>
					<v-spacer />
				</v-expansion-panel-title>
				<v-expansion-panel-text>
					<!-- Show as Percentage Share -->
					<SettingsRow title="Show as percentage share">
						<Switch
							v-model="store.document.is_visualised_as_percentage_share"
							hide-details
						/>
					</SettingsRow>

					<!-- Synchronise primary and secondary vertical axis -->
					<SettingsRow title="Synchronise y-axis">
						<Switch :disabled="disableSyncYAxis" v-model="synchronisedYAxis" />
					</SettingsRow>

					<!-- Set Custom Boundaries -->
					<SettingsRow title="Set custom boundaries">
						<Switch v-model="store.document.is_custom_boundaries_enabled" />
					</SettingsRow>

					<!-- Custom Boundaries Input -->
					<v-expand-transition>
						<settings-row-input
							v-if="store.document.is_custom_boundaries_enabled"
							class="setting-details-format"
						>
							<div
								style="
									display: grid;
									grid-template-columns: 1fr 1fr;
									gap: 0.5rem;
								"
							>
								<locale-number-input
									v-model="customBoundaryMin"
									:format="store.derivedDocument.formatForGraph"
									label="Min value"
									density="compact"
									:rules="[required(t)]"
									hide-details
									clearable
									@click:clear="() => (customBoundaryMin = 0)"
								/>

								<locale-number-input
									v-model="customBoundaryMax"
									:format="store.derivedDocument.formatForGraph"
									label="Max value"
									density="compact"
									:rules="[required(t)]"
									:precision="null"
									hide-details
									clearable
									@click:clear="() => (customBoundaryMax = 0)"
								/>
							</div>
						</settings-row-input>
					</v-expand-transition>
				</v-expansion-panel-text>
			</v-expansion-panel>

			<!-- Horizontal Axis Panel -->
			<v-expansion-panel
				class="mt-0 expansion-panel-border"
				elevation="0"
				:key="'horizontal'"
			>
				<v-expansion-panel-title class="setting-header-format">
					<span>Horizontal Axis</span>
					<v-spacer />
				</v-expansion-panel-title>
				<v-expansion-panel-text>
					<!-- Sort Horizontal by Value -->
					<SettingsRow title="Sort entries">
						<Switch
							v-model="store.document.is_horizontal_sorting_enabled"
							hide-details
						/>
					</SettingsRow>
					<v-expand-transition>
						<settings-row-input
							v-if="store.document.is_horizontal_sorting_enabled"
							class="setting-details-format"
						>
							<v-select
								density="compact"
								v-model="store.document.horizontal_sorting_direction"
								:items="[
									{ name: 'Ascending', value: 'asc' },
									{ name: 'Descending', value: 'desc' },
								]"
								item-title="name"
								item-value="value"
								variant="filled"
								hide-details
							/>
						</settings-row-input>
					</v-expand-transition>

					<!--					limit the horizontal/legend options to a maximum of x values-->
					<!-- Show Top-X Only -->
					<SettingsRow
						v-if="store.document.is_horizontal_sorting_enabled"
						title="Limit entries"
					>
						<Switch
							v-model="store.document.is_horizontal_top_x_enabled"
							hide-details
						/>
					</SettingsRow>

					<v-expand-transition>
						<settings-row-input
							v-if="
								store.document.is_horizontal_top_x_enabled &&
								store.document.is_horizontal_sorting_enabled
							"
							class="setting-details-format"
						>
							<v-number-input
								v-model="horizontal_top_x"
								hide-details
								density="compact"
								:min="1"
							/>
							<div class="d-flex pt-1">
								<div class="w-100 text-caption pt-1 text-medium-emphasis">
									Group remaining entries as "other"
								</div>
								<div class="pt-1">
									<Switch
										v-model="
											isAggregateHorizontalRemainingCategoriesEnabledVisible
										"
										:disabled="!isHorizontalAggregationAvailable"
										hide-details
									/>
								</div>
							</div>
						</settings-row-input>
					</v-expand-transition>
				</v-expansion-panel-text>
			</v-expansion-panel>

			<!-- Legend Panel -->
			<v-expansion-panel
				class="mt-0 expansion-panel-border"
				elevation="0"
				:key="'legend'"
			>
				<v-expansion-panel-title class="setting-header-format">
					<span>Legend</span>
					<v-spacer />
				</v-expansion-panel-title>
				<v-expansion-panel-text>
					<!-- Show Legend -->
					<SettingsRow title="Show legend">
						<Switch v-model="store.document.is_legend_visible" hide-details />
					</SettingsRow>

					<v-expand-transition>
						<settings-row-input
							v-if="store.document.is_legend_visible"
							class="setting-details-format"
						>
							<v-select
								density="compact"
								v-if="store.document.is_legend_visible"
								v-model="store.document.legend_position"
								:items="legendOptions"
								item-title="name"
								item-value="id"
								variant="filled"
								hide-details
							/>
						</settings-row-input>
					</v-expand-transition>

					<!-- Aggregate after   -->
					<SettingsRow title="Limit options">
						<Switch
							:disabled="!isAggregateLegendAvailable"
							v-model="aggregateLegend"
							hide-details
						/>
					</SettingsRow>

					<v-expand-transition>
						<settings-row-input
							v-if="aggregateLegend"
							class="setting-details-format"
						>
							<v-number-input
								v-model="aggregate_legend_after"
								hide-details="auto"
								density="compact"
								:min="0"
							/>
						</settings-row-input>
					</v-expand-transition>
				</v-expansion-panel-text>
			</v-expansion-panel>

			<!-- Other Settings Panel -->
			<v-expansion-panel class="mt-0 expansion-panel-border" elevation="0">
				<v-expansion-panel-title class="setting-header-format">
					<span>Other Settings</span>
					<v-spacer />
				</v-expansion-panel-title>
				<v-expansion-panel-text>
					<!-- Show Zero Values -->
					<SettingsRow title="Show zero values for dates">
						<Switch
							v-model="store.document.should_show_zero_values_for_date_time"
							hide-details
						/>
					</SettingsRow>

					<!-- Show Delta -->
					<SettingsRow title="Show delta">
						<Switch v-model="showDelta" hide-details />
					</SettingsRow>

					<v-expand-transition>
						<settings-row-input v-if="showDelta" class="setting-details-format">
							<v-select
								v-model="store.document.date_comparison.isDeltaAbsolute"
								:items="deltaOptions"
								item-title="name"
								item-value="id"
								density="compact"
								hide-details="auto"
							/>
						</settings-row-input>
					</v-expand-transition>
					<v-expand-transition>
						<SettingsRow v-if="showDelta" title="Show as secondary vertical">
							<Switch
								v-model="showDeltaAsSecondary"
								hide-details
								:disabled="disableShowDeltaAsSecondaryVertical"
							/>
						</SettingsRow>
					</v-expand-transition>

					<!-- Show Annotations -->
					<SettingsRow title="Show annotations">
						<Switch
							v-model="store.document.is_annotation_visibility_enabled"
							hide-details
						/>
					</SettingsRow>

					<SettingsRow title="Hierarchy naming style" />
					<settings-row-input class="setting-details-format">
						<v-select
							density="compact"
							v-model="store.document.hierarchy_naming_style"
							:items="hierarchyNamingStyleOptions"
							item-title="name"
							item-value="id"
							variant="filled"
							hide-details
						/>
					</settings-row-input>

					<SettingsRow title="Booking window (days)" />
					<v-expand-transition>
						<settings-row-input class="setting-details-format">
							<v-combobox
								v-model="period_build_up_window"
								:items="periodItems"
								:rules="[required(t), numericAndFloat(t)]"
								variant="filled"
								hide-details="auto"
								density="compact"
							/>
						</settings-row-input>
					</v-expand-transition>

					<SettingsRow
						v-if="showTransactionBuildUpRange"
						title="Transaction range (days)"
					/>
					<v-expand-transition v-if="showTransactionBuildUpRange">
						<settings-row-input class="setting-details-format">
							<div class="transaction-range">
								<v-number-input
									v-model="transaction_build_up_days_before"
									label="Before"
									:min="0"
									control-variant="stacked"
									variant="filled"
									density="compact"
									hide-details
								/>
								<v-number-input
									v-model="transaction_build_up_days_after"
									label="After"
									:min="0"
									control-variant="stacked"
									variant="filled"
									density="compact"
									hide-details
								/>
							</div>
						</settings-row-input>
					</v-expand-transition>

					<!-- Use new build-up query -->
					<SettingsRow title="Use new build-up query">
						<Switch
							v-model="store.document.use_new_build_up_query"
							hide-details
						/>
					</SettingsRow>
				</v-expansion-panel-text>
			</v-expansion-panel>
		</v-expansion-panels>
	</perfect-scrollbar>
</template>

<style>
.v-expansion-panels .v-expansion-panel-text__wrapper {
	padding: 0.5rem 1rem !important;
}

.setting-header-format {
	height: 36px;
	font-size: 1rem;
	font-weight: 400;
	background-color: rgb(var(--color-secondary));
}

.setting-details-format {
	padding-left: 3px;
}

.transaction-range {
	display: flex;
	gap: 0.5rem;
}

.transaction-range > * {
	flex: 1 1 0;
	min-width: 0;
}
</style>
